import { AppDispatch } from "../../store";
import {
  setValidationErrors,
  clearValidationErrors,
} from "../../store/slices/pulseCheckSlice";
import { Animated } from "react-native";

// Define a type that can represent form values with string keys and string values
type PulseCheckFormValues = {
  [key: string]: string;
};

/**
 * Validates the PulseCheck form and shows appropriate error messages
 * @param formValues - Form values from Redux store
 * @param dispatch - Redux dispatch function
 * @param showStatusMessage - Function to display status messages
 * @param scrollToTop - Function to scroll to top of page
 * @param progress - Animated value for progress indicator
 * @returns boolean indicating if form is valid
 */
export const validateForm = (
  formValues: PulseCheckFormValues,
  dispatch: AppDispatch,
  showStatusMessage: (type: "success" | "error", message: string) => void,
  scrollToTop: () => void,
  progress: Animated.Value
): boolean => {
  // Get the Redux state to check signature fields and observation type
  const state = require("../../store").default.getState();
  const { 
    managerSignatureFileName, 
    employeeSignatureFileName, 
    metGoal
  } = state.pulseCheck;
  const observationType = state.observation.selectedFilters.observationType;

  // Only validate metric/result fields for Pulse Check observation type
  // For other types (Routine, Full), these validations are not needed
  let required: (keyof typeof formValues)[] = [];

  if (observationType === "Pulse Check") {
    required = ["metric1", "result1", "target1", "result2"];
  }

  const emptyFields = required.filter((f) => !formValues[f]?.trim());

  // Check if at least one signature is present (only for non-Pulse Check types)
  const hasSignature = managerSignatureFileName || employeeSignatureFileName;
  const requireSignature = observationType !== "Pulse Check";
  const signatureNeededButMissing = requireSignature && !hasSignature;

  // Check if metGoal is selected (required only for Pulse Check observation type)
  const metGoalMissing = observationType === "Pulse Check" && metGoal === null;

  // Check if we have any validation errors
  if (emptyFields.length > 0 || signatureNeededButMissing || metGoalMissing) {
    // Create validation errors object
    const validationErrors = emptyFields.reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {} as Record<string, boolean>);

    // Add signature validation error if needed for non-Pulse Check types
    if (signatureNeededButMissing) {
      validationErrors.signature = true;
    }

    // Add metGoal validation error if needed
    if (metGoalMissing) {
      validationErrors.metGoal = true;
    }

    // Set validation errors in Redux store
    dispatch(setValidationErrors(validationErrors));

    // Show detailed error message based on missing fields
    const errorFields = emptyFields.map((field) => {
      switch (field) {
        case "metric1":
          return "Metric (First Check)";
        case "result1":
          return "Last Shift Results (First Check)";
        case "target1":
          return "Target (First Check)";
        // case 'metric2' removed as it's now populated from metric1
        case "result2":
          return "Results (Second Check)";
        default:
          return field;
      }
    });

    // Add signature error message if needed for non-Pulse Check types
    if (signatureNeededButMissing) {
      errorFields.push("At least one signature (Manager or Employee)");
    }

    // Add metGoal error message if needed
    if (metGoalMissing) {
      errorFields.push("Met Goal selection");
    }

    // Join all error fields
    const errorFieldsText = errorFields.join(", ");

    // Show error message and scroll to top
    showStatusMessage(
      "error",
      `Please fill all required fields: ${errorFieldsText}`
    );

    // Extra call to scrollToTop to ensure reliable scrolling
    setTimeout(scrollToTop, 200);

    return false; // Form is invalid
  } else {
    // Clear any previous validation errors
    dispatch(clearValidationErrors());

    Animated.timing(progress, {
      toValue: 1,
      duration: 500,
      useNativeDriver: false,
    }).start();

    return true; // Form is valid
  }
};
